import { Injectable, Injector } from '@angular/core';
import { logAction } from '../common/actionLog';
import { findById } from '../common/baseUtils';
import { emptyIcon } from '../common/icons';
import { Command, CommandEvent, CommandParams, ToolSource } from '../common/interfaces';
import { setLastToolSource } from '../common/toolUtils';
import { Editor } from './editor';
import { IFeatureFlagService } from './feature-flag.service.interface';

export const baseCommand: Command = {
  group: '',
  id: '',
  name: '',
  description: '',
  video: undefined,
  icons: [emptyIcon],
  dontPreventDefault: false,
  allowRepeatedKey: false,
  highlight: false,
  nonFatal: false,
  canExecute: () => true,
  execute: () => { },
};

export function canExecute(command: Command | undefined, params: CommandParams, featureFlags: IFeatureFlagService): command is Command {
  if (!command) return false;
  if (command.feature && !featureFlags.isFeatureSupported(command.feature)) return false;
  return command.canExecute(params);
}

@Injectable({ providedIn: 'any' })
export class CommandService {
  constructor(private injector: Injector, private featureFlags: IFeatureFlagService) { }
  onError = (_message: string, _error: Error) => { };
  private commands: Command[] = [];
  add(command: Partial<Command>) {
    this.commands.push({ ...baseCommand, ...command });
  }
  get(id: string): Command | undefined {
    return findById(this.commands, id);
  }
  getAll() {
    return this.commands;
  }
  highlight(commandId: string, time = 5000) {
    const command = this.get(commandId);

    if (command && !command.highlight) {
      command.highlight = true;
      setTimeout(() => command.highlight = false, time);
    }
  }
  canExecute(command: Command | undefined, params: CommandParams) {
    return canExecute(command, params, this.featureFlags);
  }
  async executeCommand(command: Command | undefined, params: CommandParams, source: ToolSource, event?: CommandEvent) {
    try {
      if (canExecute(command, params, this.featureFlags)) {
        logAction(`[local] command: ${command.id}`);
        setLastToolSource(source);
        const editor = this.injector.get(Editor);
        editor.textTool.confirmDeferredChange();
        await command.execute(params, event);
      }
    } catch (e) {
      if (command && command.nonFatal) {
        this.onError(`Failed to ${command.name} (${e.message || e})`, e);
      } else {
        this.onError(e.message, e);
      }
    }
  }
  async executeCommandById(id: string, params: CommandParams, source: ToolSource) {
    const command = findById(this.commands, id);

    if (!command) {
      throw new Error(`Invalid command Id: ${id}`);
    } else {
      await this.executeCommand(command, params, source);
    }
  }
}
